"use client";
import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import MateriasTable from './MateriasTable';
import NewMateriaModal from './NewMateriaModal';
import { createSubject, getSubjects, getSubject, updateSubject, deleteSubject } from '../../api/subjects';
import LoaderOverlay from '../../components/LoaderOverlay';
import Toast from '../DocentesScreen/Toast';
import styles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';
import estudiantesStyles from '../../styles/EstudiantesStyles/Estudiantes.module.css';
import { useSidebar } from '../../components/Sidebar';
import { usePermissions } from '../../hooks/usePermissions';


const PantallaDeMaterias = () => {
  const { isCollapsed } = useSidebar();
  const { canCreate, canEdit, canView } = usePermissions();
  const puedeCrear =
    canCreate('Gestión Escolar') ||
    canEdit('Gestión Escolar') ||
    canView('Gestión Escolar');

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create'); // 'create' | 'view' | 'edit'
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [materias, setMaterias] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const data = await getSubjects({ page: 1, pageSize: 2000 });
        if (mounted) setMaterias(data.results || []);
      } catch (e) {
        console.error('Error cargando materias:', e);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const handleView = async (materia) => {
    const id = materia.id_materia || materia.id;
    if (!id) {
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'ID de materia no válido' } }));
      return;
    }
    try {
      setLoading(true);
      const full = await getSubject(id);
      setSelected(full);
      setMode('view');
      setOpen(true);
    } catch (e) {
      console.error('Error cargando materia:', e);
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'No se pudo cargar la materia' } }));
    } finally {
      setLoading(false);
    }
  };

  // Filtrado en tiempo real de materias (similar a asesores y programas)
  const filteredMaterias = useMemo(() => {
    if (!query.trim()) return materias;

    const term = query.toLowerCase().trim();
    return materias.filter((m) => {
      const nombre = (m.nombre || m.name || '').toLowerCase();
      const clave = (m.clave || m.code || '').toLowerCase();
      const seccion = (m.seccion_nombre || m.section || '').toLowerCase();
      const academia = (m.academia_nombre || m.academy || '').toLowerCase();

      return (
        nombre.includes(term) ||
        clave.includes(term) ||
        seccion.includes(term) ||
        academia.includes(term)
      );
    });
  }, [materias, query]);

  const handleSearch = async () => {
    // Además del filtro local en memoria, refrescamos desde el backend
    // cuando el usuario pulsa Buscar o Enter (comportamiento previo).
    try {
      setLoading(true);
      console.log('Buscando materias con término (server-side):', query);
      const data = await getSubjects({ page: 1, pageSize: 2000, search: query });
      setMaterias(data.results || []);
    } catch (e) {
      console.error('Error buscando materias:', e);
      window.dispatchEvent(new CustomEvent('toast:show', {
        detail: { type: 'error', message: 'No se pudieron cargar las materias filtradas' }
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (materia) => {
    const id = materia.id_materia || materia.id;
    if (!id) {
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'ID de materia no válido' } }));
      return;
    }
    try {
      setLoading(true);
      const full = await getSubject(id);
      setSelected(full);
      setMode('edit');
      setOpen(true);
    } catch (e) {
      console.error('Error cargando materia:', e);
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'No se pudo cargar la materia' } }));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (materia) => {
    const id = materia.id_materia || materia.id;
    if (!id) {
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: 'ID de materia no válido' } }));
      return;
    }
    if (!window.confirm(`¿Eliminar la materia "${materia.nombre}"?`)) return;
    try {
      setLoading(true);
      await deleteSubject(id);
      const updated = await getSubjects({ page: 1, pageSize: 2000 });
      setMaterias(updated.results || []);
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'success', message: 'Materia eliminada' } }));
    } catch (e) {
      console.error('Error eliminando materia:', e);
      window.dispatchEvent(new CustomEvent('toast:show', {
        detail: {
          type: 'error',
          message: e?.message || 'No se pudo eliminar la materia (puede tener información relacionada).'
        }
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      setLoading(true);
      const materiaId = selected?.id_materia || selected?.id;
      if (mode === 'edit' && materiaId) {
        await updateSubject(materiaId, formData);
        window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'success', message: 'Materia actualizada' } }));
      } else {
        // Crear materia y usar la respuesta mapeada para agregarla a la lista actual
        const createdRow = await createSubject(formData);
        window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'success', message: 'Materia creada' } }));
        setMaterias((prev) => Array.isArray(prev) ? [...prev, createdRow] : [createdRow]);
      }
      setOpen(false);
      setSelected(null);
      setMode('create');
    } catch (err) {
      console.error('Error guardando materia:', err);
      window.dispatchEvent(new CustomEvent('toast:show', { detail: { type: 'error', message: `No se pudo guardar la materia. ${err?.message || ''}` } }));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpen(false);
    setSelected(null);
    setMode('create');
  };

  return (
    <div className={styles.mainContainer}>
      <div className={`${styles.layout} ${isCollapsed ? styles.sidebarCollapsedLayout : ''}`}>
        <div className={styles.sidebarColumn}>
          <Sidebar />
        </div>
        <div className={styles.contentColumn}>
          <HeaderGlobal title="Materias" />
          <main className={styles.mainContent}>
            <div className={styles.contentArea}>
              <div className={styles.contentWrapper} style={{ position: 'relative' }}>
                <LoaderOverlay visible={loading} />
                <div className={estudiantesStyles.searchSection}>
                  <div className={estudiantesStyles.searchCard}>
                    <div className={styles.programsSearchRow}>
                      <div className={estudiantesStyles.searchInputGroup}>
                        <svg
                          className={estudiantesStyles.searchIcon}
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                            stroke="#898d8d"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <input
                          type="text"
                          className={estudiantesStyles.searchInput}
                          placeholder="Buscar Materia o Clave..."
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleSearch();
                          }}
                        />
                      </div>

                      <button
                        className={styles.buscarButtonProgramas}
                        onClick={handleSearch}
                      >
                        Buscar
                      </button>

                      {puedeCrear && (
                        <button
                          className={styles.newProgramButton}
                          onClick={() => { setMode('create'); setSelected(null); setOpen(true); }}
                        >
                          + Nueva Materia
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.innerScrollArea}>
                  <MateriasTable
                    query={query}
                    materias={filteredMaterias}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    loading={loading}
                    isSearching={!!query}
                  />
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
      <NewMateriaModal
        open={open}
        mode={mode}
        onClose={handleCloseModal}
        readOnly={mode === 'view'}
        initialData={selected}
        onSubmit={handleSubmit}
      />
      <Toast />
    </div>
  );
};

export default PantallaDeMaterias;
